import { CustomizationResponse } from '~/api/types/customisationData.ts';
import { getRequestConfig, withMakeRequest } from '~/api';
import Request from '~/api/Request.ts';
import { AuthMethodData } from '~/api/types/authMethodData.ts';
import { HostedAuthPageTypes } from '~/pages/ExternalPages/commons/utils.tsx';
import {
  SwitcherOrganizationData,
  SwitcherUserDetailsData
} from '~/api/types/orgSwitcherData.ts';
import { OrganizationAuthFeaturesMapType } from '~/api/types/featureFlagData.ts';
import camelcaseKeys from 'camelcase-keys';
import { AuthRequestStates } from '~/api/types/hostedAuthData.ts';

const getHostedAuthCustomization: () => Promise<CustomizationResponse> =
  withMakeRequest(
    async () => await Request.getInstance().get(`/auth:customizations`)
  );

type GetAuthMethodsForEnvironmentRequest = {
  intent: HostedAuthPageTypes;
};

type GetAuthMethodsForEnvironmentResponse = {
  authMethods: AuthMethodData[];
};

const getAuthMethodsForEnvironment: (
  request: GetAuthMethodsForEnvironmentRequest
) => Promise<AuthMethodData[]> = withMakeRequest(
  async ({ intent }: GetAuthMethodsForEnvironmentRequest) =>
    await Request.getInstance().get(
      `/authmethods?intent=${intent}`,
      getRequestConfig({ addEnvDomain: true })
    ),
  (data: GetAuthMethodsForEnvironmentResponse) => data.authMethods
);

type DiscoverAuthMethodRequest = {
  email: string;
  intent: HostedAuthPageTypes;
};

type DiscoverAuthMethodResponse = {
  authMethod: AuthMethodData;
};

const discoverAuthMethodForEmail: (
  request: DiscoverAuthMethodRequest
) => Promise<AuthMethodData> = withMakeRequest(
  async (request: DiscoverAuthMethodRequest) =>
    await Request.getInstance().post(
      `/auth:discovery`,
      request,
      getRequestConfig({ addEnvDomain: true })
    ),
  (data: DiscoverAuthMethodResponse) => data.authMethod
);

type VerifyPasswordLessAuthMethodRequest = {
  codeChallenge: string;
};

const verifyPasswordLessAuthMethod: (
  request: VerifyPasswordLessAuthMethodRequest
) => Promise<boolean> = withMakeRequest(
  async (request: VerifyPasswordLessAuthMethodRequest) =>
    await Request.getInstance().post(
      `/auth/passwordless:verify`,
      request,
      getRequestConfig({ addEnvDomain: true })
    ),
  () => true
);

const resendPasswordLessEmail: () => Promise<boolean> = withMakeRequest(
  async () =>
    await Request.getInstance().post(
      `/auth/passwordless:resend`,
      {},
      getRequestConfig({ addEnvDomain: true })
    ),
  () => true
);

type GetSwitcherOrganizationMappingResponse = {
  organizations: SwitcherOrganizationData;
  user: SwitcherUserDetailsData;
  intent: HostedAuthPageTypes;
};

const getSwitcherOrganizationMapping: () => Promise<GetSwitcherOrganizationMappingResponse> =
  withMakeRequest(
    async () =>
      await Request.getInstance().get(
        `/auth:organizations`,
        getRequestConfig({ addEnvDomain: true })
      )
  );

type GetOrganizationAuthFeaturesResponse = {
  features: OrganizationAuthFeaturesMapType;
};

const getOrganizationAuthFeatures: () => Promise<GetOrganizationAuthFeaturesResponse> =
  withMakeRequest(
    async () =>
      await Request.getInstance().get(
        `/auth:features`,
        getRequestConfig({ addEnvDomain: true, camelCaseResponse: false })
      ),
    ({ features, ...rest }: GetOrganizationAuthFeaturesResponse) =>
      ({
        features,
        ...camelcaseKeys(rest, { deep: true })
      }) as GetOrganizationAuthFeaturesResponse
  );

type SignupUserRequest = {
  organizationName?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
};

type SignupUserResponse = {
  organizationId: string;
  userId: string;
  organizationName?: string;
};

const signupUser: (request: SignupUserRequest) => Promise<SignupUserResponse> =
  withMakeRequest(
    async (request: SignupUserRequest) =>
      await Request.getInstance().post(
        `/auth:signup`,
        request,
        getRequestConfig({ addEnvDomain: true })
      )
  );

type GetActiveAuthStateResponse = {
  authState: AuthRequestStates;
};

const getActiveAuthState: () => Promise<AuthRequestStates> = withMakeRequest(
  async () =>
    await Request.getInstance().get(
      `/auth/state`,
      getRequestConfig({ addEnvDomain: true })
    ),
  (data: GetActiveAuthStateResponse) => data.authState
);

const logoutFromOrgSwitcher: () => Promise<boolean> = withMakeRequest(
  async () =>
    await Request.getInstance().post(
      `/auth/logout`,
      {},
      getRequestConfig({ addEnvDomain: true })
    ),
  () => true
);

type GetAuthErrorResponse = {
  error: string;
  errorDescription: string;
};

const getAuthError: (errorId: string) => Promise<GetAuthErrorResponse> =
  withMakeRequest(
    async (errorId: string) =>
      await Request.getInstance().get(
        `/errors/${errorId}`,
        getRequestConfig({ addEnvDomain: true })
      ),
    (data: GetAuthErrorResponse) => data
  );

export {
  getHostedAuthCustomization,
  getAuthMethodsForEnvironment,
  discoverAuthMethodForEmail,
  verifyPasswordLessAuthMethod,
  resendPasswordLessEmail,
  getSwitcherOrganizationMapping,
  signupUser,
  getActiveAuthState,
  getOrganizationAuthFeatures,
  logoutFromOrgSwitcher,
  getAuthError
};
